import React, { useState } from "react";
import { Box, Paper, Typography, Button, ButtonGroup } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type TimeRange = "7days" | "30days" | "90days" | "year";

interface EarningsChartProps {
  data?: {
    [key in TimeRange]: Array<{ name: string; earnings: number }>;
  };
}

const defaultData = {
  "7days": [
    { name: "Mon", earnings: 80 },
    { name: "Tue", earnings: 120 },
    { name: "Wed", earnings: 100 },
    { name: "Thu", earnings: 150 },
    { name: "Fri", earnings: 180 },
    { name: "Sat", earnings: 220 },
    { name: "Sun", earnings: 160 },
  ],
  "30days": [
    { name: "Week 1", earnings: 120 },
    { name: "Week 2", earnings: 180 },
    { name: "Week 3", earnings: 240 },
    { name: "Week 4", earnings: 300 },
  ],
  "90days": [
    { name: "Month 1", earnings: 680 },
    { name: "Month 2", earnings: 840 },
    { name: "Month 3", earnings: 920 },
  ],
  year: [
    { name: "Jan", earnings: 420 },
    { name: "Feb", earnings: 560 },
    { name: "Mar", earnings: 640 },
    { name: "Apr", earnings: 720 },
    { name: "May", earnings: 840 },
    { name: "Jun", earnings: 840 },
    { name: "Jul", earnings: 0 },
    { name: "Aug", earnings: 0 },
    { name: "Sep", earnings: 0 },
    { name: "Oct", earnings: 0 },
    { name: "Nov", earnings: 0 },
    { name: "Dec", earnings: 0 },
  ],
};

const EarningsChart: React.FC<EarningsChartProps> = ({
  data = defaultData,
}) => {
  const [timeRange, setTimeRange] = useState<TimeRange>("30days");

  const currentData = data[timeRange];

  const formatYAxis = (value: number) => `$${value}`;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            backgroundColor: "white",
            border: "1px solid #E0E0E0",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ${payload[0].value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {payload[0].payload.name}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Earnings Overview
        </Typography>

        <ButtonGroup size="small">
          <Button
            variant={timeRange === "7days" ? "contained" : "outlined"}
            onClick={() => setTimeRange("7days")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            7 Days
          </Button>
          <Button
            variant={timeRange === "30days" ? "contained" : "outlined"}
            onClick={() => setTimeRange("30days")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            30 Days
          </Button>
          <Button
            variant={timeRange === "90days" ? "contained" : "outlined"}
            onClick={() => setTimeRange("90days")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            90 Days
          </Button>
          <Button
            variant={timeRange === "year" ? "contained" : "outlined"}
            onClick={() => setTimeRange("year")}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Year
          </Button>
        </ButtonGroup>
      </Box>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={currentData}
          margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
          <XAxis
            dataKey="name"
            stroke="#666"
            style={{ fontSize: "12px" }}
          />
          <YAxis
            tickFormatter={formatYAxis}
            stroke="#666"
            style={{ fontSize: "12px" }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(23, 139, 142, 0.1)" }} />
          <Bar
            dataKey="earnings"
            fill="#178B8E"
            radius={[8, 8, 0, 0]}
            maxBarSize={60}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default EarningsChart;
